import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Button, 
  List, 
  Avatar, 
  Badge, 
  Typography, 
  Select, 
  Space, 
  Progress,
  Table,
  Tag,
  Divider
} from 'antd';
import { 
  UserOutlined, 
  MedicineBoxOutlined, 
  BankOutlined, 
  CalendarOutlined, 
  PlusOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useTranslation } from '../hooks/useTranslation';

const { Title, Text } = Typography;
const { Option } = Select;

const Dashboard: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const { t } = useTranslation();

  // Enhanced mock data
  const stats = [
    {
      title: t('dashboard.totalUsers'),
      value: 1234,
      precision: 0,
      prefix: <UserOutlined style={{ color: '#1890ff' }} />,
      suffix: '',
      valueStyle: { color: '#1890ff' },
      change: 12,
      changeType: 'positive' as const
    },
    {
      title: t('dashboard.totalDoctors'),
      value: 89,
      precision: 0,
      prefix: <MedicineBoxOutlined style={{ color: '#52c41a' }} />,
      suffix: '',
      valueStyle: { color: '#52c41a' },
      change: 5,
      changeType: 'positive' as const
    },
    {
      title: t('dashboard.totalClinics'),
      value: 23,
      precision: 0,
      prefix: <BankOutlined style={{ color: '#722ed1' }} />,
      suffix: '',
      valueStyle: { color: '#722ed1' },
      change: 2,
      changeType: 'positive' as const
    },
    {
      title: t('dashboard.todayAppointments'),
      value: 156,
      precision: 0,
      prefix: <CalendarOutlined style={{ color: '#fa8c16' }} />,
      suffix: '',
      valueStyle: { color: '#fa8c16' },
      change: 8,
      changeType: 'positive' as const
    }
  ];

  const recentActivities = [
    {
      id: 1,
      title: t('dashboard.drSarahJohnson'),
      description: t('dashboard.newAppointmentScheduled'),
      time: t('dashboard.twoMinutesAgo'),
      status: 'success',
      avatar: 'SJ'
    },
    {
      id: 2,
      title: t('dashboard.system'),
      description: t('dashboard.newUserRegistered', { email: 'john.doe@example.com' }),
      time: t('dashboard.fiveMinutesAgo'),
      status: 'processing',
      avatar: 'S'
    },
    {
      id: 3,
      title: t('dashboard.drMichaelChen'),
      description: t('dashboard.updatedAvailabilitySchedule'),
      time: t('dashboard.tenMinutesAgo'),
      status: 'warning',
      avatar: 'MC'
    },
    {
      id: 4,
      title: t('dashboard.centralMedicalClinic'),
      description: t('dashboard.addedNewServices'),
      time: t('dashboard.fifteenMinutesAgo'),
      status: 'success',
      avatar: 'C'
    }
  ];

  const quickActions = [
    {
      title: t('doctors.addDoctor'),
      description: t('dashboard.registerNewDoctor'),
      icon: <MedicineBoxOutlined />,
      href: '/doctors',
      color: '#1890ff'
    },
    {
      title: t('appointments.addAppointment'),
      description: t('dashboard.scheduleNewAppointment'),
      icon: <CalendarOutlined />,
      href: '/appointments',
      color: '#52c41a'
    },
    {
      title: t('clinics.addClinic'),
      description: t('dashboard.registerNewClinic'),
      icon: <BankOutlined />,
      href: '/clinics',
      color: '#722ed1'
    },
    {
      title: t('patients.title'),
      description: t('dashboard.browsePatientRecords'),
      icon: <UserOutlined />,
      href: '/patients',
      color: '#fa8c16'
    }
  ];

  const performanceMetrics = [
    {
      title: t('dashboard.responseTime'),
      value: 2.3,
      suffix: t('dashboard.seconds'),
      change: -0.5,
      changeType: 'positive' as const
    },
    {
      title: t('dashboard.successRate'),
      value: 98.5,
      suffix: '%',
      change: 2.1,
      changeType: 'positive' as const
    },
    {
      title: t('dashboard.activeSessions'),
      value: 45,
      suffix: '',
      change: 12,
      changeType: 'positive' as const
    },
    {
      title: t('dashboard.revenue'),
      value: 12.5,
      prefix: '$',
      suffix: 'K',
      change: 8.2,
      changeType: 'positive' as const
    }
  ];

  const topDoctors = [
    { name: t('dashboard.drSarahJohnson'), appointments: 45, rating: 4.9 },
    { name: t('dashboard.drMichaelChen'), appointments: 38, rating: 4.8 },
    { name: t('dashboard.drEmilyDavis'), appointments: 32, rating: 4.7 }
  ];

  const upcomingAppointments = [
    { time: '09:00 AM', patient: 'John Doe', doctor: t('dashboard.drSarahJohnson') },
    { time: '10:30 AM', patient: 'Jane Smith', doctor: t('dashboard.drMichaelChen') },
    { time: '02:00 PM', patient: 'Bob Wilson', doctor: t('dashboard.drEmilyDavis') }
  ];

  return (
    <div>
      {/* Header Section */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>{t('dashboard.title')}</Title>
          <Text type="secondary">{t('dashboard.welcomeBack')}</Text>
        </Col>
        <Col>
          <Space>
            <Select 
              value={selectedPeriod} 
              onChange={setSelectedPeriod}
              style={{ width: 120 }}
            >
              <Option value="today">{t('dashboard.today')}</Option>
              <Option value="week">{t('dashboard.thisWeek')}</Option>
              <Option value="month">{t('dashboard.thisMonth')}</Option>
              <Option value="year">{t('dashboard.thisYear')}</Option>
            </Select>
            <Button type="primary" icon={<PlusOutlined />}>
              {t('dashboard.quickAction')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card>
              <Statistic
                title={stat.title}
                value={stat.value}
                precision={stat.precision}
                valueStyle={stat.valueStyle}
                prefix={stat.prefix}
                suffix={stat.suffix}
              />
              <div style={{ marginTop: 8 }}>
                <Text type={stat.changeType === 'positive' ? 'success' : 'danger'}>
                  {stat.changeType === 'positive' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                  {stat.change}%
                </Text>
                <Text type="secondary" style={{ marginLeft: 8 }}>{t('dashboard.vsLastPeriod')}</Text>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Performance Metrics */}
      <Card title={t('dashboard.statistics')} style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          {performanceMetrics.map((metric, index) => (
            <Col xs={12} md={6} key={index}>
              <div style={{ textAlign: 'center' }}>
                <Statistic
                  title={metric.title}
                  value={metric.value}
                  prefix={metric.prefix}
                  suffix={metric.suffix}
                  valueStyle={{ fontSize: '20px' }}
                />
                <div style={{ marginTop: 4 }}>
                  <Text type={metric.changeType === 'positive' ? 'success' : 'danger'} style={{ fontSize: '12px' }}>
                    {metric.changeType === 'positive' ? '+' : ''}{metric.change}
                    {typeof metric.change === 'number' && metric.suffix ? metric.suffix : '%'}
                  </Text>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {/* Quick Actions */}
        <Col xs={24} lg={12}>
          <Card 
            title={t('dashboard.quickActions')} 
            extra={<Link to="/dashboard">{t('dashboard.viewAll')}</Link>}
          >
            <Row gutter={[8, 8]}>
              {quickActions.map((action, index) => (
                <Col xs={24} sm={12} key={index}>
                  <Link to={action.href}>
                    <Card 
                      size="small" 
                      hoverable
                      style={{ height: '100%' }}
                    >
                      <Space>
                        <Avatar 
                          style={{ backgroundColor: action.color }} 
                          icon={action.icon}
                        />
                        <div>
                          <div style={{ fontWeight: 500 }}>{action.title}</div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {action.description}
                          </Text>
                        </div>
                      </Space>
                    </Card>
                  </Link>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>

        {/* Recent Activity */}
        <Col xs={24} lg={12}>
          <Card 
            title={t('dashboard.recentActivity')} 
            extra={<Link to="/dashboard">{t('dashboard.viewAll')}</Link>}
          >
            <List
              itemLayout="horizontal"
              dataSource={recentActivities}
              renderItem={item => (
                <List.Item 
                  actions={[
                    <Button type="link" icon={<EyeOutlined />} size="small" />
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Badge 
                        status={item.status as any}
                        dot
                      >
                        <Avatar size="small">{item.avatar}</Avatar>
                      </Badge>
                    }
                    title={item.title}
                    description={
                      <div>
                        <div>{item.description}</div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {item.time}
                        </Text>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>

      {/* Additional Insights */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card title={t('dashboard.topPerformingDoctors')}>
            <List
              dataSource={topDoctors}
              renderItem={(doctor, index) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Avatar style={{ backgroundColor: '#1890ff' }}>
                        {index + 1}
                      </Avatar>
                    }
                    title={doctor.name}
                    description={t('dashboard.appointments', { count: doctor.appointments })}
                  />
                  <div>
                    <Text strong>{doctor.rating}</Text>
                    <span style={{ color: '#faad14', marginLeft: 4 }}>★</span>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card title={t('dashboard.todayAppointments')}>
            <List
              dataSource={upcomingAppointments}
              renderItem={appointment => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<Avatar icon={<ClockCircleOutlined />} />}
                    title={appointment.time}
                    description={
                      <div>
                        <div>{appointment.patient}</div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {appointment.doctor}
                        </Text>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard; 